import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { AffiliationsDataGroup } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";

export interface AffiliationsDataResult {
  affiliationsData: AffiliationsData[];
  affiliationsDataLoading: boolean;
}

export const AFFILIATIONS_DATA_QUERY_KEY_PREFIX = "benchmarking-affiliations-data";

const FAKE_CONTRACT_ENTITY_COUNT = 10;

// if the user does not have the provider affiliations insights role, we still want to display a blurred-out
// version of the widget, so synthesize data in the same shape the real endpoint would return
const buildFakeAffiliationsData = (selectedNetworks: BenchmarkingNetworkType[]): AffiliationsData[] => {
  const fakeContractEntities: AffiliationsData[] = [];

  for (let i = 0; i < FAKE_CONTRACT_ENTITY_COUNT; i++) {
    const contracts: AffiliationsDataGroup[] = [];

    for (const x of selectedNetworks) {
      contracts.push({
        affiliationsCount: Math.floor(Math.random() * (5000 - 1)) + 1,
        contractId: x.contractId,
        versionId: x.versionId,
      });
    }

    fakeContractEntities.push({
      contractEntityName: `Quest Contract Entity ${i + 1}`,
      contracts,
      professionalContractEntityId: i + 1,
    });
  }

  return fakeContractEntities;
}

export const useAffiliationsData = (
  service: BenchmarkingServiceType,
  payload: NetworkComparisonFiltersPayload | null,
  isAffiliationState: boolean,
  selectedNetworks: BenchmarkingNetworkType[]
): AffiliationsDataResult => {
  const queryResult = useQuery<AffiliationsData[]>({
    enabled: !!payload && isAffiliationState,
    queryFn: ({ signal }) => service.getAffiliationsData(payload, signal),
    queryKey: [AFFILIATIONS_DATA_QUERY_KEY_PREFIX, service?.lineOfBusiness, JSON.stringify(payload)],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const fakeAffiliationsData = useMemo<AffiliationsData[]>(() => {
    if (isAffiliationState || !payload) {
      return [];
    }

    return buildFakeAffiliationsData(selectedNetworks);
    // intentionally keyed on payload (not selectedNetworks) so each applied payload gets a fresh random snapshot
  }, [isAffiliationState, payload]);

  return {
    affiliationsData: isAffiliationState ? (queryResult.data ?? []) : fakeAffiliationsData,
    affiliationsDataLoading: isAffiliationState ? queryResult.isFetching : false,
  };
}

export default useAffiliationsData;
